import { message, Modal } from 'antd';
import React, { useMemo } from 'react';
import { RootState } from '../../../../store/store';
import { useSelector } from 'react-redux';
import useGetGrossPrice from '../../../../hooks/useGetGrossPrice';
import { summarizeOfferFare } from '@skygroup/shared/utils/fareFamilies';
import FareOptionCard from '../../../ViewFlight/FareUpgrade/FareOptionCard';

interface Props {
  isFareModalOpen: boolean;
  setIsFareModalOpen: (value: React.SetStateAction<boolean>) => void;
  isFlightFrom?: boolean;
  additionalFares: any;
  setDynamicFlights: any;
  fareName: any;
  fareIdentifier: string;
  /**
   * The segments of the one-way currently selected, to mark which ticket is
   * already chosen. (`fareIdentifier` was always undefined — it was read off a
   * segment ARRAY — so the "selected" highlight never showed for a ticket whose
   * legs carry different fare names.)
   */
  currentSegments?: any[];
}

/**
 * The other tickets for ONE flight of a two-one-ways selection. Same card as
 * the view page's "Upgrade your ticket" (FareOptionCard): the supplier's own
 * service list instead of keyword-guessed icons.
 */
const AdditionalFaresFlightModal = ({
  isFareModalOpen,
  setIsFareModalOpen,
  isFlightFrom,
  additionalFares,
  setDynamicFlights,
  fareName,
  currentSegments,
}: Props) => {
  const { viewFlight: viewFlight_t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const getGrossPrice = useGetGrossPrice();
  const { direction } = useSelector((data: RootState) => data.config);

  const currentSignature = useMemo(
    () =>
      currentSegments?.length
        ? summarizeOfferFare({ flightFrom: currentSegments }).signature
        : null,
    [currentSegments]
  );

  const options = useMemo(() => {
    const list = additionalFares ? Object.values(additionalFares) : [];
    return (list as any[])
      .map((flight: any) => {
        const summary = summarizeOfferFare(flight);
        const isCurrent = currentSignature
          ? summary.signature === currentSignature
          : !summary.isMixed && summary.names[0] === fareName;
        const price =
          Number(
            getGrossPrice(
              flight?.price?.grandTotal ?? flight?.price?.total,
              flight?.agent_additional_info?.suggested_commission
            )
          ) || 0;
        return { flight, summary, isCurrent, price };
      })
      .sort(
        (a, b) =>
          Number(a.summary.isMixed) - Number(b.summary.isMixed) ||
          a.price - b.price
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalFares, currentSignature, fareName]);

  const basePrice = options.find((option) => option.isCurrent)?.price;

  const choose = (flight: any) => {
    setDynamicFlights((prev: any) => {
      const newFlights = [...prev];
      if (isFlightFrom) newFlights[0] = flight;
      else newFlights[1] = flight;
      return newFlights;
    });
    setIsFareModalOpen(false);
    message.success(viewFlight_t.fareChangedSuccessfully);
  };

  return (
    <Modal
      open={isFareModalOpen}
      footer={null}
      onCancel={() => {
        setIsFareModalOpen(false);
      }}
      width={820}
      className={`additional-fares-modal additional-fares-modal-${direction}`}
    >
      <div className="fare-upgrade-header">
        <div>
          <h3>{viewFlight_t.upgradeTicketTitle}</h3>
          <p>{viewFlight_t.upgradeTicketSubtitle}</p>
        </div>
      </div>

      <div className="fare-modal-options">
        {options.map(({ flight, summary, isCurrent, price }) => {
          const sign = flight?.price?.currency_sign ?? '';
          const money = (value: number) =>
            `${Math.round(value).toLocaleString()}${String.fromCharCode(160)}${sign}`;
          const diff =
            basePrice !== undefined && !isCurrent ? price - basePrice : null;
          return (
            <FareOptionCard
              key={flight.identifier}
              summary={summary}
              totalLabel={money(price)}
              diffLabel={
                diff ? `${diff > 0 ? '+' : '−'} ${money(Math.abs(diff))}` : undefined
              }
              isCurrent={isCurrent}
              badge={isCurrent ? viewFlight_t.yourTicket : undefined}
              ctaLabel={viewFlight_t.chooseThisTicket}
              onChoose={isCurrent ? undefined : () => choose(flight)}
            />
          );
        })}
      </div>

      <ul className="fare-upgrade-notes">
        {viewFlight_t.allFaresComment1 && <li>{viewFlight_t.allFaresComment1}</li>}
        {viewFlight_t.allFaresComment2 && <li>{viewFlight_t.allFaresComment2}</li>}
      </ul>
    </Modal>
  );
};

export default AdditionalFaresFlightModal;
